import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Bot, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/EmptyState';
import { ActivityStream } from '@/components/ActivityStream';
import { ApiError } from '@/lib/fetcher';
import {
  type AgentConnection,
  connectionsListKey,
  fetchConnections,
  revokeConnection,
} from '@/lib/queries';

/**
 * Brief 328 / D335 — where this connection's authorization codes are delivered.
 *
 * `label` is the DCR-supplied client_name: self-declared, unverified, and free
 * to forge, so on its own this list cannot tell a rogue connection from the one
 * it impersonates. The destination is decoded from the signed client_id and is
 * the attacker's own address, so it cannot be faked. Shown directly under the
 * name because the pairing IS the signal.
 */
function Destinations({ conn }: { conn: AgentConnection }) {
  const dests = conn.destinations ?? [];
  if (dests.length === 0) return null;
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      {dests.map((d) => (
        <span
          key={d.display}
          className="font-mono text-xs text-muted-foreground"
          title={
            d.kind === 'loopback'
              ? 'Software running on your computer'
              : d.kind === 'app'
                ? 'An app installed on your device'
                : d.kind === 'unparseable'
                  ? "We couldn't read this address — treat it as untrusted"
                  : 'A website on the internet'
          }
        >
          {d.display}
        </span>
      ))}
    </div>
  );
}

/** Micros → USD. Sub-cent precise (6dp, trimmed); zero renders "$0.00". */
function formatSpend(microsStr: string): string {
  // `?? '0'` guards a deploy-skew window (new SPA vs an API build without the
  // field) so a missing value renders "$0.00" instead of throwing in BigInt().
  const micros = BigInt(microsStr ?? '0');
  if (micros === 0n) return '$0.00';
  const dollars = micros / 1_000_000n;
  const remainder = micros % 1_000_000n;
  if (remainder === 0n) return `$${dollars}.00`;
  let frac = remainder.toString().padStart(6, '0').replace(/0+$/, '');
  if (frac.length < 2) frac = frac.padEnd(2, '0');
  return `$${dollars}.${frac}`;
}

export function Connections() {
  const q = useQuery({ queryKey: connectionsListKey, queryFn: fetchConnections });
  const qc = useQueryClient();

  const [revoking, setRevoking] = React.useState<AgentConnection | null>(null);
  // Brief 185 — each agent expands to its own activity mini-stream.
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const revoke = useMutation({
    mutationFn: revokeConnection,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: connectionsListKey });
      setRevoking(null);
    },
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Connected agents</h1>
        <p className="text-muted-foreground">
          Agents you've authorized over MCP OAuth. Each can spend your prepaid
          balance, read and delete your stored files, create share links for
          them, sign contracts on your behalf, and read your Courier messages —
          it cannot mint API keys, register webhooks, or change how you sign in.
          The address under each name is where it receives its authorization;
          the name itself is self-declared. Revoke any agent here to cut off its
          access.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Active connections</CardTitle>
          <CardDescription>
            Spend is the total each agent has drawn from your balance. Revoking
            stops the agent from refreshing; its current access token expires
            within 24 hours.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load connections (
                {q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => q.refetch()}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : q.data && q.data.data.length === 0 ? (
            <EmptyState
              icon={Bot}
              title="No connected agents yet"
              description="Add this dashboard's MCP server (api.relaystation.ai/mcp) as a connector in Claude Code, Codex, or any MCP client, then approve it in your browser. The agent shows up here."
            />
          ) : q.data ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Agent</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.data.map((conn) => (
                  <React.Fragment key={conn.id}>
                    <TableRow>
                      <TableCell className="pr-0">
                        <button
                          type="button"
                          onClick={() => toggle(conn.id)}
                          className="text-muted-foreground hover:text-foreground"
                          aria-label={expanded.has(conn.id) ? 'Hide activity' : 'Show activity'}
                        >
                          {expanded.has(conn.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="font-medium">
                        {conn.label ?? 'Unnamed agent'}
                        <Destinations conn={conn} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {conn.scope.split(/\s+/).filter(Boolean).map((s) => (
                            <Badge key={s} variant="secondary">
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {format(parseISO(conn.createdAt), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {conn.lastUsedAt
                          ? format(parseISO(conn.lastUsedAt), 'MMM d, yyyy')
                          : 'Never'}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {formatSpend(conn.spentMicros)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRevoking(conn)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="mr-1 h-4 w-4" />
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expanded.has(conn.id) ? (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/20">
                          <div className="px-2 py-1">
                            <p className="mb-2 text-xs font-medium text-muted-foreground">
                              {conn.label ?? 'This agent'}'s recent activity
                            </p>
                            <ActivityStream
                              baseQuery={{ actor: conn.id }}
                              labels={{ connections: { [conn.id]: conn.label ?? 'Connected agent' } }}
                              pageSize={5}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>

      <AlertDialog
        open={Boolean(revoking)}
        onOpenChange={(o) => !o && setRevoking(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Revoke {revoking?.label ?? 'this agent'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {revoking?.destinations?.length ? (
                <>
                  This connection sends its authorization to{' '}
                  <span className="font-mono">
                    {revoking.destinations.map((d) => d.display).join(', ')}
                  </span>
                  .{' '}
                </>
              ) : null}
              It will stop being able to spend your balance — its next token
              refresh fails, and its current access token expires within 24
              hours. The agent can re-connect later by approving again in your
              browser. Other connected agents are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (revoking) revoke.mutate(revoking.id);
              }}
              disabled={revoke.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revoke.isPending ? 'Revoking…' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
